import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDataHubClient } from '../../context/DataHubClientContext.js';
import type { DataEntry } from '../../services/dataHubService.interface.js';
import { TestSetService } from '../../services/testSetService.js';

export interface UseTestSetEntriesParams {
  testSetId: string;
  version: number;
  pageSize?: number;
}

export interface UseTestSetEntriesResult {
  entries: DataEntry[];
  totalCount: number | null;
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number | null;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  goToNextPage: () => void;
  goToPrevPage: () => void;
  setPageSize: (size: number) => void;
  pageSize: number;
  refetch: () => void;
}

const DEFAULT_PAGE_SIZE = 10;

export function useTestSetEntries(
  params: UseTestSetEntriesParams,
): UseTestSetEntriesResult {
  const client = useDataHubClient();
  const service = useMemo(() => new TestSetService(client), [client]);

  const [entries, setEntries] = useState<DataEntry[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(
    params.pageSize ?? DEFAULT_PAGE_SIZE,
  );
  const [fetchTick, setFetchTick] = useState(0);

  const pageTokens = useRef<(string | null)[]>([null]);

  useEffect(() => {
    setCurrentPage(1);
    pageTokens.current = [null];
    setTotalCount(null);
  }, [params.testSetId, params.version]);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    const token = pageTokens.current[currentPage - 1] ?? undefined;

    service
      .listTestSetData({
        testSetId: params.testSetId,
        version: params.version,
        pageSize,
        continuationToken: token,
        includeCount: true,
      })
      .then((result) => {
        if (cancelled) return;

        setEntries(result.items);
        setTotalCount(result.totalCount);

        if (
          result.continuationToken !== null &&
          pageTokens.current[currentPage] === undefined
        ) {
          pageTokens.current[currentPage] = result.continuationToken;
        }

        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load entries');
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    service,
    params.testSetId,
    params.version,
    currentPage,
    pageSize,
    fetchTick,
  ]);

  const totalPages =
    totalCount !== null ? Math.ceil(totalCount / pageSize) : null;
  const hasNextPage =
    pageTokens.current[currentPage] !== undefined ||
    (totalPages !== null && currentPage < totalPages);
  const hasPrevPage = currentPage > 1;

  const goToNextPage = useCallback(() => {
    setCurrentPage((p) => p + 1);
  }, []);

  const goToPrevPage = useCallback(() => {
    setCurrentPage((p) => Math.max(1, p - 1));
  }, []);

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setCurrentPage(1);
    pageTokens.current = [null];
  }, []);

  const refetch = useCallback(() => {
    setCurrentPage(1);
    pageTokens.current = [null];
    setFetchTick((t) => t + 1);
  }, []);

  return {
    entries,
    totalCount,
    isLoading,
    error,
    currentPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
    goToNextPage,
    goToPrevPage,
    setPageSize,
    pageSize,
    refetch,
  };
}
